class StickyAddToCartOptions extends HTMLElement {
  constructor() {
    super();

    this.resizeTimeout = null;
    this.syncTimeout = null;
    this.syncInterval = null;

    this._data = null;
    this._meta = null;

    this._allLoaded = false;
    this._loadingAllVariants = false;

    this._abortController = null;
    this._priceAbortController = null;

    this._lastMainVariantId = null;
    this._lastStickyVariantId = null;
  }


  /* =========================================================
     BASIC ROOT HELPERS
  ========================================================= */

  getStickyRoot() {
    return this.closest('sticky-add-to-cart') || document;
  }


  getStickySection() {
    const stickyRoot = this.getStickyRoot();

    return (
      stickyRoot.closest('[id^="shopify-section-"]') ||
      stickyRoot.parentElement ||
      document
    );
  }


  /**
   * Find the REAL main product section.
   *
   * Important:
   * We intentionally do NOT use the sticky section ID here.
   */
  getMainProductRoot() {
    const stickyRoot = this.getStickyRoot();

    const candidates = Array.from(
      document.querySelectorAll('[id^="MainProduct-"]')
    );

    for (const root of candidates) {
      if (stickyRoot.contains(root)) continue;
      if (root.closest('sticky-add-to-cart')) continue;

      const hasProductForm = root.querySelector(
        '.product-form input[name="id"], .product-form select[name="id"]'
      );

      if (hasProductForm) {
        return root;
      }
    }


    const productInfoCandidates = Array.from(
      document.querySelectorAll('product-info')
    );

    for (const root of productInfoCandidates) {
      if (root.closest('sticky-add-to-cart')) continue;

      if (
        root.querySelector(
          '.product-form input[name="id"], .product-form select[name="id"]'
        )
      ) {
        return root;
      }
    }


    const main = document.querySelector('main');

    if (main) {
      const productForm = main.querySelector(
        '.product-form input[name="id"], .product-form select[name="id"]'
      );

      if (productForm && !productForm.closest('sticky-add-to-cart')) {
        return main;
      }
    }


    return document;
  }


  getMainSectionId() {
    const root = this.getMainProductRoot();

    if (!root || root === document) return null;


    if (root.id && root.id.startsWith('MainProduct-')) {
      return root.id.replace('MainProduct-', '');
    }


    if (root.dataset) {
      if (root.dataset.sectionId) return root.dataset.sectionId;
      if (root.dataset.section) return root.dataset.section;
    }


    const shopifySection = root.closest('[id^="shopify-section-"]');

    if (shopifySection?.id) {
      return shopifySection.id.replace('shopify-section-', '');
    }


    return null;
  }


  getMainVariantElement() {
    const root = this.getMainProductRoot();

    if (!root) return null;


    const inputs = Array.from(
      root.querySelectorAll(
        '.product-form input[name="id"], .product-form select[name="id"]'
      )
    );


    return (
      inputs.find(el => !el.closest('sticky-add-to-cart')) ||
      null
    );
  }


  getMainVariantId() {
    const element = this.getMainVariantElement();

    if (!element) return null;

    return element.value ? String(element.value) : null;
  }


  getStickyVariantInput() {
    const root = this.getStickyRoot();

    return root.querySelector('[data-sticky-variant-id]');
  }


  getStickySelect() {
    return this.querySelector('[data-sticky-variant-select]');
  }


  /* =========================================================
     LIFECYCLE
  ========================================================= */

  connectedCallback() {
    this.cleanupListeners();

    this._abortController = new AbortController();

    this.generateOptions();
    this.setupListeners();

    this.queueSynchronization(0);
    this.queueSynchronization(100);
    this.queueSynchronization(300);


    /**
     * Some themes update the hidden variant input without firing
     * a native change event.
     *
     * Lightweight polling guarantees the sticky bar stays synced.
     */
    this.syncInterval = window.setInterval(() => {
      this.checkMainVariantState();
    }, 500);
  }


  disconnectedCallback() {
    this.cleanupListeners();
  }


  cleanupListeners() {
    if (this.resizeTimeout) {
      clearTimeout(this.resizeTimeout);
      this.resizeTimeout = null;
    }

    if (this.syncTimeout) {
      clearTimeout(this.syncTimeout);
      this.syncTimeout = null;
    }

    if (this.syncInterval) {
      clearInterval(this.syncInterval);
      this.syncInterval = null;
    }

    if (this._abortController) {
      this._abortController.abort();
      this._abortController = null;
    }

    if (this._priceAbortController) {
      this._priceAbortController.abort();
      this._priceAbortController = null;
    }
  }


  /* =========================================================
     LISTENERS
  ========================================================= */

  setupListeners() {
    const signal = this._abortController.signal;
    const stickySelect = this.getStickySelect();


    if (stickySelect) {
      const triggerLazyLoad = () => {
        this.ensureAllVariantsLoaded();
      };


      stickySelect.addEventListener(
        'focus',
        triggerLazyLoad,
        { once: true, signal }
      );

      stickySelect.addEventListener(
        'mousedown',
        triggerLazyLoad,
        { once: true, signal }
      );

      stickySelect.addEventListener(
        'touchstart',
        triggerLazyLoad,
        { once: true, passive: true, signal }
      );


      stickySelect.addEventListener(
        'change',
        event => {
          const variantId = event.target.value;

          if (variantId) {
            this.handleStickyVariantChange(variantId);
          }
        },
        { signal }
      );
    }


    /**
     * Listen to ALL native changes inside main product area.
     *
     * This supports:
     * radio variants
     * dropdown variants
     * swatches
     * hidden variant ID changes that dispatch change
     */
    const mainRoot = this.getMainProductRoot();

    if (mainRoot && mainRoot !== document) {
      mainRoot.addEventListener(
        'change',
        () => {
          this.queueSynchronization(0);
          this.queueSynchronization(80);
          this.queueSynchronization(180);
        },
        {
          capture: true,
          signal
        }
      );
    }


    /**
     * Common Shopify / theme variant events.
     */
    document.addEventListener(
      'shopify:variant:change',
      event => {
        this.handleExternalVariantEvent(event);
      },
      { signal }
    );


    document.addEventListener(
      'variant:change',
      event => {
        this.handleExternalVariantEvent(event);
      },
      { signal }
    );


    document.addEventListener(
      'product:variant-change',
      event => {
        this.handleExternalVariantEvent(event);
      },
      { signal }
    );


    /**
     * Shopify theme editor section reload.
     */
    document.addEventListener(
      'shopify:section:load',
      () => {
        this._data = null;
        this._meta = null;

        this.queueSynchronization(100);
        this.queueSynchronization(300);
      },
      { signal }
    );


    document.addEventListener(
      'shopify:section:select',
      () => {
        this.queueSynchronization(100);
      },
      { signal }
    );


    window.addEventListener(
      'resize',
      () => {
        if (this.resizeTimeout) {
          clearTimeout(this.resizeTimeout);
        }

        this.resizeTimeout = setTimeout(() => {
          this.synchronizeWithMainForm();
        }, 250);
      },
      { signal }
    );
  }


  queueSynchronization(delay = 0) {
    window.setTimeout(() => {
      this.synchronizeWithMainForm();
    }, delay);
  }


  checkMainVariantState() {
    const currentId = this.getMainVariantId();

    if (!currentId) return;


    if (String(currentId) !== String(this._lastMainVariantId)) {
      this._lastMainVariantId = String(currentId);

      this.synchronizeWithMainForm();
    }
  }


  async handleExternalVariantEvent(event) {
    const detail = event?.detail || {};

    const variantId =
      detail.variantId ||
      detail.variant?.id ||
      detail.id ||
      null;


    if (!variantId) {
      this.queueSynchronization(30);
      return;
    }


    let variant = this.getVariantById(variantId);


    if (!variant) {
      await this.ensureAllVariantsLoaded();

      variant = this.getVariantById(variantId);
    }


    /**
     * Ignore events for products whose variant ID does not exist
     * in this sticky product.
     */
    if (!variant) return;


    this._lastMainVariantId = String(variant.id);

    this.updateStickyDisplay(variant);
    this.updateStickyVariantId(variant.id);
    this.updateStickySelect(variant.id);
  }


  /* =========================================================
     MAIN -> STICKY SYNCHRONIZATION
  ========================================================= */

  async synchronizeWithMainForm() {
    const currentVariantId = this.getMainVariantId();

    if (!currentVariantId) {
      return;
    }


    let variant = this.getVariantById(currentVariantId);


    if (!variant) {
      await this.ensureAllVariantsLoaded();

      variant = this.getVariantById(currentVariantId);
    }


    if (!variant) {
      return;
    }


    this._lastMainVariantId = String(variant.id);


    this.updateStickyVariantId(variant.id);
    this.updateStickySelect(variant.id);
    this.updateStickyDisplay(variant);
  }


  /* =========================================================
     STICKY -> MAIN SYNCHRONIZATION
  ========================================================= */

  async handleStickyVariantChange(variantId) {
    let variant = this.getVariantById(variantId);


    if (!variant) {
      await this.ensureAllVariantsLoaded();

      variant = this.getVariantById(variantId);
    }


    if (!variant) {
      console.warn(
        '[Sticky Add To Cart] Variant not found:',
        variantId
      );

      return;
    }


    try {
      const productData = this.getProductData();

      const optionsMeta = Array.isArray(productData?.options)
        ? productData.options
        : [];


      const root = this.getMainProductRoot();


      /**
       * Update main product option controls.
       */
      optionsMeta.forEach((optionMeta, index) => {
        const value = variant[`option${index + 1}`];

        if (value == null) return;


        const expectedName = `options[${optionMeta.name}]`;


        /**
         * Dropdown option selectors
         */
        const selects = Array.from(
          root.querySelectorAll('select')
        ).filter(select => {
          return (
            select.name === expectedName &&
            !select.closest('sticky-add-to-cart')
          );
        });


        selects.forEach(select => {
          if (String(select.value) !== String(value)) {
            select.value = value;

            select.dispatchEvent(
              new Event('input', {
                bubbles: true
              })
            );

            select.dispatchEvent(
              new Event('change', {
                bubbles: true
              })
            );
          }
        });


        /**
         * Radio / swatch option selectors
         */
        const radios = Array.from(
          root.querySelectorAll('input[type="radio"]')
        ).filter(radio => {
          return (
            radio.name === expectedName &&
            String(radio.value) === String(value) &&
            !radio.closest('sticky-add-to-cart')
          );
        });


        radios.forEach(radio => {
          if (!radio.checked) {
            radio.checked = true;

            radio.dispatchEvent(
              new Event('input', {
                bubbles: true
              })
            );

            radio.dispatchEvent(
              new Event('change', {
                bubbles: true
              })
            );
          }
        });
      });


      /**
       * Update main form's actual variant ID as fallback.
       *
       * This is important for themes where option controls
       * do not immediately update hidden input[name="id"].
       */
      const mainVariantFields = Array.from(
        root.querySelectorAll(
          '.product-form input[name="id"], .product-form select[name="id"]'
        )
      ).filter(el => !el.closest('sticky-add-to-cart'));


      mainVariantFields.forEach(field => {
        if (String(field.value) !== String(variant.id)) {
          field.value = String(variant.id);

          field.dispatchEvent(
            new Event('change', {
              bubbles: true
            })
          );
        }
      });


      /**
       * Update sticky immediately.
       */
      this._lastMainVariantId = String(variant.id);

      this.updateStickyVariantId(variant.id);
      this.updateStickySelect(variant.id);
      this.updateStickyDisplay(variant);


      /**
       * Give the main theme time to finish its own variant logic,
       * then re-read the final variant ID.
       */
      this.queueSynchronization(80);
      this.queueSynchronization(180);

    } catch (error) {
      console.error(
        '[Sticky Add To Cart] Error handling sticky variant change:',
        error
      );
    }
  }


  /* =========================================================
     PRODUCT DATA
  ========================================================= */

  getProductData() {
    if (this._data) {
      return this._data;
    }


    try {
      const section = this.getStickySection();


      const element =
        section.querySelector?.('[data-sticky-product-options]') ||
        document.querySelector('[data-sticky-product-options]');


      this._data = element
        ? JSON.parse(element.textContent)
        : null;


      if (!this._data) {
        return null;
      }


      this._data.variant_options =
        Array.isArray(this._data.variant_options)
          ? this._data.variant_options
          : [];


      /**
       * Make sure current variant always exists in local list.
       *
       * This fixes selected variants that are outside the first
       * 250 Liquid variants.
       */
      const currentVariant = this._data.current_variant;


      if (currentVariant?.id) {
        const exists = this._data.variant_options.some(
          variant =>
            String(variant.id) === String(currentVariant.id)
        );


        if (!exists) {
          this._data.variant_options.unshift(currentVariant);
        }
      }


      return this._data;

    } catch (error) {
      console.error(
        '[Sticky Add To Cart] Cannot parse sticky product data:',
        error
      );

      return null;
    }
  }


  getVariantMeta() {
    if (this._meta) {
      return this._meta;
    }


    try {
      const section = this.getStickySection();


      const element =
        section.querySelector?.('[data-sticky-variant-data]') ||
        document.querySelector('[data-sticky-variant-data]');


      this._meta = element
        ? JSON.parse(element.textContent)
        : null;


      return this._meta;

    } catch (error) {
      console.warn(
        '[Sticky Add To Cart] Cannot parse variant metadata:',
        error
      );

      return null;
    }
  }


  getVariantById(variantId) {
    if (!variantId) return null;


    const data = this.getProductData();

    if (!data) return null;


    return (
      data.variant_options || []
    ).find(
      variant =>
        String(variant.id) === String(variantId)
    ) || null;
  }


  /* =========================================================
     GENERATE SELECT
  ========================================================= */

  generateOptions() {
    const productData = this.getProductData();

    if (!productData) return;


    const selectField = this.getStickySelect();


    let currentVariant = productData.current_variant;


    const mainVariantId = this.getMainVariantId();


    if (mainVariantId) {
      const mainVariant = this.getVariantById(mainVariantId);

      if (mainVariant) {
        currentVariant = mainVariant;
      }
    }


    if (selectField) {
      this.populateSelectOptions(
        productData.variant_options,
        selectField,
        currentVariant
      );


      const meta = this.getVariantMeta();


      if (
        meta &&
        meta.variants_count &&
        productData.variant_options.length < meta.variants_count
      ) {
        this.insertLoadingPlaceholder(selectField);
      }
    }


    if (currentVariant) {
      this.updateStickyVariantId(currentVariant.id);
      this.updateStickySelect(currentVariant.id);
      this.updateStickyDisplay(currentVariant);
    }
  }


  populateSelectOptions(
    variants,
    selectField,
    currentVariant
  ) {
    if (!selectField) return;


    selectField.innerHTML = '';


    const unavailableText =
      (
        window.variantStrings &&
        (
          window.variantStrings.unavailable ||
          window.variantStrings.soldOut
        )
      ) ||
      'Unavailable';


    variants.forEach(variant => {
      if (!variant?.id) return;


      const option = document.createElement('option');

      option.value = String(variant.id);


      let imageUrl = '';


      if (variant.featured_image) {
        imageUrl =
          variant.featured_image.src ||
          variant.featured_image.url ||
          variant.featured_image ||
          '';
      }


      if (
        imageUrl &&
        imageUrl.includes('cdn.shopify.com')
      ) {
        imageUrl =
          imageUrl.split('?')[0] +
          '?width=80';
      }


      if (imageUrl) {
        option.setAttribute(
          'data-img',
          imageUrl
        );
      }


      option.setAttribute(
        'data-available',
        variant.available ? 'true' : 'false'
      );


      if (
        currentVariant &&
        String(variant.id) === String(currentVariant.id)
      ) {
        option.selected = true;
      }


      option.textContent = variant.available
        ? variant.title
        : `${variant.title} - ${unavailableText}`;


      if (!variant.available) {
        option.classList.add('option-disabled');
      }


      selectField.appendChild(option);
    });
  }


  insertLoadingPlaceholder(selectField) {
    if (!selectField) return;


    if (
      selectField.querySelector(
        'option[data-loading]'
      )
    ) {
      return;
    }


    const option = document.createElement('option');

    option.value = '';
    option.dataset.loading = 'true';
    option.textContent = 'Loading variants…';

    selectField.appendChild(option);
  }


  removeLoadingPlaceholder(selectField) {
    if (!selectField) return;


    selectField
      .querySelectorAll('option[data-loading]')
      .forEach(option => option.remove());
  }


  /* =========================================================
     UPDATE STICKY VARIANT ID
  ========================================================= */

  updateStickyVariantId(variantId) {
    if (!variantId) return;


    const input = this.getStickyVariantInput();


    if (input) {
      input.value = String(variantId);
      input.setAttribute(
        'value',
        String(variantId)
      );
    }


    this._lastStickyVariantId =
      String(variantId);
  }


  updateStickySelect(variantId) {
    if (!variantId) return;


    const select = this.getStickySelect();


    if (!select) return;


    const exists = Array.from(select.options).some(
      option =>
        String(option.value) === String(variantId)
    );


    if (exists) {
      select.value = String(variantId);
    }
  }


  /* =========================================================
     STICKY DISPLAY
  ========================================================= */

  updateStickyDisplay(variant) {
    if (!variant?.id) return;


    try {
      const stickyRoot = this.getStickyRoot();


      /**
       * Always update the submitted variant ID first.
       */
      this.updateStickyVariantId(variant.id);


      /**
       * Image
       */
      const stickyImage =
        stickyRoot.querySelector(
          '[data-sticky-product-image]'
        );


      if (stickyImage) {
        const featuredImage =
          variant.featured_image;


        let imageSrc = null;
        let imageAlt = '';


        if (featuredImage) {
          if (typeof featuredImage === 'string') {
            imageSrc = featuredImage;
          } else {
            imageSrc =
              featuredImage.src ||
              featuredImage.url ||
              null;

            imageAlt =
              featuredImage.alt ||
              variant.title ||
              '';
          }
        }


        /**
         * If selected variant has no image,
         * fall back to main product image.
         */
        if (!imageSrc) {
          const data = this.getProductData();

          imageSrc =
            data?.featured_image?.src ||
            null;

          imageAlt =
            data?.featured_image?.alt ||
            variant.title ||
            '';
        }


        if (imageSrc) {
          let url = imageSrc;


          if (
            url.includes('cdn.shopify.com')
          ) {
            url =
              url.split('?')[0] +
              '?width=200';
          }


          if (stickyImage.src !== url) {
            stickyImage.src = url;
          }


          if (imageAlt) {
            stickyImage.alt = imageAlt;
          }
        }
      }


      /**
       * Price
       */
      this.updateStickyPriceFromServer(
        variant.id
      );


      /**
       * Add to cart state
       */
      const button =
        stickyRoot.querySelector(
          'button[name="add"]'
        );


      if (button) {
        const span =
          button.querySelector('span');


        const addText =
          button.dataset.addToCart ||
          window.variantStrings?.addToCart ||
          'Add to cart';


        const soldOutText =
          button.dataset.soldOut ||
          window.variantStrings?.soldOut ||
          'Sold out';


        if (variant.available) {
          button.disabled = false;
          button.removeAttribute('disabled');

          if (span) {
            span.textContent = addText;
          }
        } else {
          button.disabled = true;
          button.setAttribute(
            'disabled',
            'disabled'
          );

          if (span) {
            span.textContent =
              soldOutText;
          }
        }
      }

    } catch (error) {
      console.error(
        '[Sticky Add To Cart] Display update error:',
        error
      );
    }
  }


  /* =========================================================
     SERVER PRICE REFRESH
  ========================================================= */

  async updateStickyPriceFromServer(variantId) {
    if (!variantId) return;


    try {
      const stickyRoot = this.getStickyRoot();


      const priceWrap =
        stickyRoot.querySelector(
          '[data-sticky-price]'
        ) ||
        stickyRoot.querySelector(
          '[id^="price-"]'
        );


      if (!priceWrap) return;


      const stickySectionId =
        stickyRoot.dataset.stickySectionId ||
        priceWrap.id.replace('price-', '');


      if (!stickySectionId) return;


      const handle =
        this.getProductData()?.handle;


      if (!handle) return;


      /**
       * Cancel older request when user changes variants quickly.
       */
      if (this._priceAbortController) {
        this._priceAbortController.abort();
      }


      this._priceAbortController =
        new AbortController();


      const url = new URL(
        `/products/${encodeURIComponent(handle)}`,
        window.location.origin
      );


      url.searchParams.set(
        'variant',
        String(variantId)
      );

      url.searchParams.set(
        'section_id',
        stickySectionId
      );


      const response = await fetch(
        url.toString(),
        {
          headers: {
            'X-Requested-With':
              'XMLHttpRequest'
          },

          signal:
            this._priceAbortController.signal
        }
      );


      if (!response.ok) {
        return;
      }


      const html =
        await response.text();


      /**
       * Do not apply an old response after user has already
       * selected another variant.
       */
      const currentStickyId =
        this.getStickyVariantInput()?.value;


      if (
        String(currentStickyId) !==
        String(variantId)
      ) {
        return;
      }


      const documentHtml =
        new DOMParser().parseFromString(
          html,
          'text/html'
        );


      const sourcePrice =
        documentHtml.getElementById(
          `price-${stickySectionId}`
        );


      if (sourcePrice) {
        priceWrap.innerHTML =
          sourcePrice.innerHTML;
      }

    } catch (error) {
      if (error?.name === 'AbortError') {
        return;
      }


      console.warn(
        '[Sticky Add To Cart] Price refresh failed:',
        error
      );
    }
  }


  /* =========================================================
     LOAD 250+ VARIANTS
  ========================================================= */

  async ensureAllVariantsLoaded() {
    if (
      this._allLoaded ||
      this._loadingAllVariants
    ) {
      return;
    }


    const data = this.getProductData();
    const meta = this.getVariantMeta();


    if (!data || !meta) {
      return;
    }


    const existingCount =
      data.variant_options.length;


    const total =
      Number(meta.variants_count) ||
      existingCount;


    if (existingCount >= total) {
      this._allLoaded = true;
      return;
    }


    this._loadingAllVariants = true;


    const selectField =
      this.getStickySelect();


    this.insertLoadingPlaceholder(
      selectField
    );


    try {
      let allVariants = null;


      /**
       * Optional custom endpoint.
       *
       * Only used if theme/site has explicitly provided
       * window.stickyVariantEndpoint.
       */
      if (
        meta.use_custom_endpoint &&
        window.stickyVariantEndpoint
      ) {
        allVariants =
          await this.fetchAllVariantsFromCustomEndpoint(
            window.stickyVariantEndpoint,
            meta
          );
      }


      /**
       * Standard Shopify fallback.
       */
      if (!allVariants) {
        allVariants =
          await this.fetchAllVariantsFromProductJS(
            meta.product_handle
          );
      }


      if (
        Array.isArray(allVariants) &&
        allVariants.length
      ) {
        const existingIds =
          new Set(
            data.variant_options.map(
              variant =>
                String(variant.id)
            )
          );


        const merged =
          data.variant_options.slice();


        for (const variant of allVariants) {
          if (!variant?.id) continue;


          if (
            !existingIds.has(
              String(variant.id)
            )
          ) {
            merged.push(variant);

            existingIds.add(
              String(variant.id)
            );
          }
        }


        this._data.variant_options =
          merged;


        const currentVariantId =
          this.getMainVariantId() ||
          this.getStickyVariantInput()?.value ||
          data.current_variant?.id;


        const currentVariant =
          this.getVariantById(
            currentVariantId
          ) ||
          data.current_variant;


        if (selectField) {
          this.populateSelectOptions(
            merged,
            selectField,
            currentVariant
          );
        }


        if (currentVariant) {
          this.updateStickyVariantId(
            currentVariant.id
          );

          this.updateStickySelect(
            currentVariant.id
          );
        }


        this._allLoaded =
          merged.length >= total;
      }

    } catch (error) {
      console.error(
        '[Sticky Add To Cart] Error loading all variants:',
        error
      );

    } finally {
      this._loadingAllVariants = false;

      this.removeLoadingPlaceholder(
        selectField
      );
    }
  }


  async fetchAllVariantsFromCustomEndpoint(
    endpoint,
    meta
  ) {
    try {
      const url = new URL(
        endpoint,
        window.location.origin
      );


      if (meta.product_id) {
        url.searchParams.set(
          'product_id',
          meta.product_id
        );
      }


      if (meta.product_handle) {
        url.searchParams.set(
          'handle',
          meta.product_handle
        );
      }


      const response = await fetch(
        url.toString(),
        {
          headers: {
            'X-Requested-With':
              'XMLHttpRequest'
          }
        }
      );


      if (!response.ok) {
        throw new Error(
          `Custom endpoint failed: ${response.status}`
        );
      }


      const json =
        await response.json();


      return this.normalizeVariantArray(
        json
      );

    } catch (error) {
      console.warn(
        '[Sticky Add To Cart] Custom endpoint failed:',
        error
      );

      return null;
    }
  }


  async fetchAllVariantsFromProductJS(handle) {
    try {
      if (!handle) {
        return null;
      }


      const response = await fetch(
        `/products/${encodeURIComponent(handle)}.js`,
        {
          headers: {
            'X-Requested-With':
              'XMLHttpRequest'
          }
        }
      );


      if (!response.ok) {
        throw new Error(
          `product.js failed: ${response.status}`
        );
      }


      const product =
        await response.json();


      const variants =
        Array.isArray(product?.variants)
          ? product.variants
          : [];


      return variants.map(variant => {
        const image =
          variant.featured_image ||
          variant.image ||
          null;


        let imageObject = null;


        if (image) {
          if (typeof image === 'string') {
            imageObject = {
              src: image,
              alt: variant.title || ''
            };
          } else if (typeof image === 'object') {
            imageObject = {
              src:
                image.src ||
                image.url ||
                '',

              alt:
                image.alt ||
                variant.title ||
                ''
            };
          }
        }


        return {
          id: variant.id,
          title: variant.title,
          price: variant.price,
          available: !!variant.available,

          option1: variant.option1,
          option2: variant.option2,
          option3: variant.option3,

          featured_image:
            imageObject
        };
      });

    } catch (error) {
      console.error(
        '[Sticky Add To Cart] product.js error:',
        error
      );

      return null;
    }
  }


  normalizeVariantArray(input) {
    let variants = input;


    /**
     * Support:
     * [variants]
     * { variants: [...] }
     */
    if (
      input &&
      !Array.isArray(input) &&
      Array.isArray(input.variants)
    ) {
      variants = input.variants;
    }


    if (!Array.isArray(variants)) {
      return null;
    }


    return variants
      .map(variant => {
        if (!variant?.id) {
          return null;
        }


        const image =
          variant.featured_image ||
          variant.image ||
          null;


        let imageObject = null;


        if (image) {
          if (typeof image === 'string') {
            imageObject = {
              src: image,
              alt:
                variant.title ||
                ''
            };
          } else {
            imageObject = {
              src:
                image.src ||
                image.url ||
                '',

              alt:
                image.alt ||
                variant.title ||
                ''
            };
          }
        }


        return {
          id: variant.id,
          title: variant.title || '',
          price: variant.price,

          available:
            typeof variant.available ===
            'boolean'
              ? variant.available
              : !!variant.available,

          option1: variant.option1,
          option2: variant.option2,
          option3: variant.option3,

          featured_image:
            imageObject
        };
      })
      .filter(Boolean);
  }


  /* =========================================================
     PUBLIC HELPERS
  ========================================================= */

  getCurrentVariant() {
    const stickyVariantId =
      this.getStickyVariantInput()?.value;


    if (!stickyVariantId) {
      return null;
    }


    return this.getVariantById(
      stickyVariantId
    );
  }


  refresh() {
    this._data = null;
    this._meta = null;

    this.generateOptions();
    this.synchronizeWithMainForm();
  }
}


/* =========================================================
   REGISTER CUSTOM ELEMENT SAFELY
========================================================= */

if (
  !customElements.get(
    'sticky-add-to-cart-options'
  )
) {
  customElements.define(
    'sticky-add-to-cart-options',
    StickyAddToCartOptions
  );
}


/* =========================================================
   FALLBACK INITIALIZATION
========================================================= */

document.addEventListener(
  'DOMContentLoaded',
  () => {
    document
      .querySelectorAll(
        'sticky-add-to-cart-options'
      )
      .forEach(element => {
        element.generateOptions?.();
        element.synchronizeWithMainForm?.();
      });
  }
);